import React, { useState } from "react";
import { useWizardContext } from "../useWizardContext";
import { registerUser, sendOtp, loginUser } from "../../../../api/auth";
import { useToast } from "../../../../context/ToastContext";
import OtpVerification from "../../../auth/steps/OtpVerification";
import { Eye, EyeOff } from "lucide-react";
import { useGoogleLogin } from "@react-oauth/google";
import FacebookLogin from "@greatsumini/react-facebook-login";
import { FACEBOOK_APP_ID } from "src/config/facebookAuth";

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

export default function Step4_ContactDetails() {
  const { 
    formData, 
    updateContact, 
    prevStep,
    setAuthenticated,
    closeWizard,
    submitEvent,
    setSuccess,
    eventId
  } = useWizardContext();
  const { showSuccess, showError } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const cd = formData.contactDetails;

  const saveUserToLocal = (token, user) => {
    if (!token || !user) return;
    localStorage.setItem("authToken", token);
    localStorage.setItem("userId", user.id || user._id);
    if (user.customerId) localStorage.setItem("customerId", user.customerId);
    if (user.professionalId) localStorage.setItem("professionalId", user.professionalId);
    localStorage.setItem("role", user.role || "");
    if (user.professionalRole) localStorage.setItem("professionalRole", user.professionalRole);
    if (user.fullName) localStorage.setItem("fullName", user.fullName);
    if (user.email) localStorage.setItem("email", user.email);
    localStorage.setItem("isVerified", String(user.isVerified || false));
    
    const isProfessional = user.role === "professional";
    const onboardingComplete = isProfessional ? !!user.professionalId : true;
    localStorage.setItem("onboardingComplete", String(onboardingComplete));
    
    if (user.phone) localStorage.setItem("phone", user.phone);
    if (user.profilePictureUrl || user.profilePicture) {
      const picUrl = user.profilePictureUrl || user.profilePicture;
      localStorage.setItem("profilePicture", picUrl);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (cd.termsAccepted === false) {
        showError("Please accept the terms of service.");
        return;
      }
      try {
        setIsLoading(true);
        const payload = {
          isGoogleAuth: true,
          googleToken: tokenResponse.access_token,
        };
        const res = await loginUser(payload);
        const userRole = (res?.user?.role || "").toLowerCase();
        if (userRole !== "customer") {
          showError("This account is registered as a professional. Please log in using a customer account to post requirements.");
          return;
        }
        saveUserToLocal(res.token, res.user);
        setAuthenticated();

        const result = await submitEvent();
        if (result) {
          const targetEventId = (typeof result === "object" ? result.eventId : null) || eventId;
          showSuccess("Logged in & Event posted successfully!");
          setSuccess(true);
          setTimeout(() => {
            closeWizard();
            if (targetEventId) {
              window.location.href = `/events/${targetEventId}`;
            } else {
              window.location.reload();
            }
          }, 1200);
        }
      } catch (err) {
        showError(err.message || "Google authentication failed.");
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      showError("Google authentication failed.");
    },
  });

  const handleFacebookResponse = async (response) => {
    if (response.status === "unknown") return;
    if (cd.termsAccepted === false) {
      showError("Please accept the terms of service.");
      return;
    }

    try {
      setIsLoading(true);
      const payload = {
        isFacebookAuth: true,
        facebookToken: response.accessToken,
      };

      const res = await loginUser(payload);
      const userRole = (res?.user?.role || "").toLowerCase();
      if (userRole !== "customer") {
        showError("This account is registered as a professional. Please log in using a customer account to post requirements.");
        return;
      }
      saveUserToLocal(res.token, res.user);
      setAuthenticated();

      const result = await submitEvent();
      if (result) {
        const targetEventId = (typeof result === "object" ? result.eventId : null) || eventId;
        showSuccess("Logged in & Event posted successfully!");
        setSuccess(true);
        setTimeout(() => {
          closeWizard();
          if (targetEventId) {
            window.location.href = `/events/${targetEventId}`;
          } else {
            window.location.reload();
          }
        }, 1200);
      }
    } catch (error) {
      showError(error.message || "Facebook authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (cd.termsAccepted === false) {
      showError("Please accept the terms of service.");
      return;
    }
    if (cd.password !== cd.confirmPassword) {
      showError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      // Instead of registering immediately, send OTP to verify email
      await sendOtp(cd.email);
      setShowOtp(true);
    } catch (error) {
      showError(error.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = async (otpData) => {
    setIsLoading(true);
    try {
      // 1. Register User with otpVerified flag
      const registerPayload = {
        fullName: `${cd.firstName} ${cd.lastName}`.trim(),
        email: cd.email,
        phone: cd.phone,
        password: cd.password,
        role: "customer", // Defaulting to customer for event posting
        otpVerified: true,
      };
      
      const res = await registerUser(registerPayload);
      const userRole = (res?.user?.role || "customer").toLowerCase();
      if (userRole !== "customer") {
        showError("This account is registered as a professional. Please log in using a customer account to post requirements.");
        return;
      }
      saveUserToLocal(res.token, res.user);
      
      setAuthenticated(); // User is now logged in

      // 2. Submit Event
      const result = await submitEvent();
      
      if (result) {
        const targetEventId = (typeof result === "object" ? result.eventId : null) || eventId;
        showSuccess("Account and Event created successfully!");
        setSuccess(true);
        setTimeout(() => {
          closeWizard();
          if (targetEventId) {
            window.location.href = `/events/${targetEventId}`;
          } else {
            window.location.reload();
          }
        }, 1200);
      }

    } catch (error) {
      showError(error.message || "Failed to submit details");
    } finally {
      setIsLoading(false);
    }
  };

  if (showOtp) {
    return (
      <div className="absolute inset-0 bg-white z-10 rounded-[28px] sm:rounded-[32px] flex items-center justify-center p-4 sm:p-6 animate-fadeIn">
        <OtpVerification
          email={cd.email}
          onVerified={handleOtpVerified}
          onBack={() => setShowOtp(false)}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-2.5 sm:gap-3 w-full animate-fadeIn flex-1 pt-1">
      {/* Social Auth Buttons at Top */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
        <FacebookLogin
          appId={FACEBOOK_APP_ID}
          onSuccess={handleFacebookResponse}
          onFail={() => {
            showError("Facebook authentication failed");
          }}
          render={({ onClick }) => (
            <button
              type="button"
              onClick={(e) => {
                if (!cd.termsAccepted) {
                  showError("Please accept the terms of service.");
                  return;
                }
                onClick(e);
              }}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 h-[36px] px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors bg-white font-normal text-gray-600 text-xs sm:text-sm disabled:opacity-50 cursor-pointer"
            >
              <FacebookIcon />
              <span>Continue with facebook</span>
            </button>
          )}
        />
        <button
          type="button"
          onClick={() => {
            if (!cd.termsAccepted) {
              showError("Please accept the terms of service.");
              return;
            }
            handleGoogleLogin();
          }}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2 h-[36px] px-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors bg-white font-normal text-gray-600 text-xs sm:text-sm disabled:opacity-50 cursor-pointer"
        >
          <GoogleIcon />
          <span>Continue with Google</span>
        </button>
      </div>

      {/* Social Auth Divider */}
      <div className="relative my-0.5 flex items-center">
        <div className="flex-grow border-t border-gray-200"></div>
        <span className="flex-shrink-0 mx-4 text-gray-400 text-xs font-normal">Or continue with</span>
        <div className="flex-grow border-t border-gray-200"></div>
      </div>

      {/* Contact Details Section */}
      <div>
        <h3 className="text-base sm:text-lg font-bold text-gray-950 mb-1.5">Contact Details</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-2.5">
          {/* First Name */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">First Name</label>
            <input
              type="text"
              required
              placeholder="Enter your first name"
              value={cd.firstName}
              onChange={(e) => updateContact({ firstName: e.target.value })}
              className="px-3 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
            />
          </div>

          {/* Last Name */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Last Name</label>
            <input
              type="text"
              required
              placeholder="Enter your last name"
              value={cd.lastName}
              onChange={(e) => updateContact({ lastName: e.target.value })}
              className="px-3 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
            />
          </div>

          {/* Contact Number */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Contact Number</label>
            <div className="flex items-center gap-2">
              <span className="w-14 sm:w-16 h-[36px] flex items-center justify-center border border-gray-200 rounded-lg bg-white text-xs sm:text-sm text-gray-900 font-normal shrink-0">
                +91
              </span>
              <input
                type="tel"
                required
                placeholder="0000000000"
                value={cd.phone}
                onChange={(e) => updateContact({ phone: e.target.value })}
                className="flex-1 px-3 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
              />
            </div>
          </div>

          {/* Email address */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Email address</label>
            <input
              type="email"
              required
              placeholder="Enter your email address"
              value={cd.email}
              onChange={(e) => updateContact({ email: e.target.value })}
              className="px-3 h-[36px] border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
            />
          </div>

          {/* Password */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                required
                placeholder="Create a password"
                value={cd.password}
                onChange={(e) => updateContact({ password: e.target.value })}
                className="w-full px-3 h-[36px] pr-10 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div className="flex flex-col gap-0.5">
            <label className="text-xs sm:text-sm font-normal text-gray-400">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                required
                placeholder="Re-enter your password"
                value={cd.confirmPassword}
                onChange={(e) => updateContact({ confirmPassword: e.target.value })}
                className="w-full px-3 h-[36px] pr-10 border border-gray-200 rounded-lg focus:outline-none focus:border-[#FF4C18] text-xs sm:text-sm text-gray-900 bg-white placeholder-gray-400"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Checkboxes */}
      <div className="flex flex-col gap-1.5 mt-0.5">
        <label className="flex items-start gap-2 cursor-pointer group">
          <input
            type="checkbox"
            className="w-3.5 h-3.5 sm:w-4 sm:h-4 accent-[#FF4C18] rounded cursor-pointer shrink-0 mt-0.5"
            checked={cd.termsAccepted !== false}
            onChange={(e) => updateContact({ termsAccepted: e.target.checked })}
          />
          <span className="text-xs sm:text-[13px] text-gray-800 font-normal leading-relaxed">
            I agree to the Terms of Service and Privacy Policy and to receive communications related to my account and activity on GoPratle.
          </span>
        </label>
        <label className="flex items-start gap-2 cursor-pointer group">
          <input
            type="checkbox"
            className="w-3.5 h-3.5 sm:w-4 sm:h-4 accent-[#FF4C18] rounded cursor-pointer shrink-0 mt-0.5"
            checked={cd.accuracyConfirmed !== false}
            onChange={(e) => updateContact({ accuracyConfirmed: e.target.checked })}
          />
          <span className="text-xs sm:text-[13px] text-gray-800 font-normal leading-relaxed">
            I agree to receive GoPratle marketing, offers, and product updates.
          </span>
        </label>
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-row justify-between items-center gap-2 mt-auto pt-2 sm:pt-3 w-full">
        <button
          type="button"
          onClick={prevStep}
          className="text-gray-400 hover:text-gray-700 font-normal text-xs sm:text-sm flex items-center gap-1.5 transition-colors cursor-pointer"
        >
          &larr; Back
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="bg-[#FF4C18] hover:bg-[#E63E0F] text-white px-6 py-2 rounded-lg font-medium text-xs sm:text-sm shadow-sm hover:shadow transition-all whitespace-nowrap disabled:opacity-50 cursor-pointer"
        >
          {isLoading ? "Sending OTP..." : "Post Requirement"}
        </button>
      </div>
    </form>
  );
}
